import React, {useEffect} from "react";
import { Card, Button, Trash} from "react-bootstrap";
import { Field, FieldArray, ErrorMessage } from 'formik';
import QRCode from "react-qr-code";
import DateField from './common/DateField ';
import TimeField from './common/TimeField';
import HierarchyField from './common/HierarchyField';
import SelectField from './common/SelectField';

  // Create initial sample object structure
  export const createInitialSample = (defaults = {}) => ({
    sampleMappingOption: "Add New Sample",
    sampleType: "",
    sampleTypeOther: "",
    sputumCollectionDetail: "",
    sampleDescription: "",
    collectionDate: "",
    collectionTime: "",
    collectionSiteState: defaults.collectionSiteState || "",
    collectionSiteDistrict: defaults.collectionSiteDistrict || "",
    collectionSiteDistrictKey: defaults.collectionSiteDistrictKey || "",
    collectionSite: "",
    collectionSiteKey: "",
    referralDate: "",
    referralSiteState: defaults.referralSiteState || "",
    referralSiteDistrict: defaults.referralSiteDistrict || "",
    referralSiteDistrictKey: defaults.referralSiteDistrictKey || "",
    referralSite: "",
    referralSiteKey: "",
    sampleSerialId: "",
    sampleQrCode: "",
    
    // Mapped Sample Fields (for existing sample mapping)
    mappedSampleId: "",
    mappedSampleSpecimenType: "",
    mappedSampleSpecimenTypeIfOther: "",
    mappedSampleSputumCollectionDetail: "",
    mappedSampleDescription: "",
    mappedSampleCollectionDate: "",
    mappedSampleCollectionTime: "",
    mappedSampleCollectionSiteState: "",
    mappedSampleCollectionSiteDistrict: "",
    mappedSampleCollectionSite: "",
    mappedSampleCollectionSiteKey: "",
    mappedSampleReferralDate: "",
    mappedSampleReferralSiteState: "",
    mappedSampleReferralSiteDistrict: "",
    mappedSampleReferralSite: "",
    mappedSampleReferralSiteKey: "",
    mappedSampleSerialId: "",
    mappedSampleQrCode: "",

    // Location keys & levels for API payload
    collectionSiteStateKey: defaults.collectionSiteStateKey || "",
    collectionSiteStateLevel: defaults.collectionSiteStateLevel || null,
    collectionSiteDistrictLevel: defaults.collectionSiteDistrictLevel || null,
    collectionSiteLevel: null,
    referralSiteStateKey: defaults.referralSiteStateKey || "",
    referralSiteStateLevel: defaults.referralSiteStateLevel || null,
    referralSiteDistrictLevel: defaults.referralSiteDistrictLevel || null,
    referralSiteLevel: null,
    mappedSampleCollectionSiteStateKey: "",
    mappedSampleCollectionSiteDistrictKey: "",
    mappedSampleCollectionSiteKey: "",
    mappedSampleReferralSiteStateKey: "",
    mappedSampleReferralSiteDistrictKey: "",
    mappedSampleReferralSiteKey: ""
  });

  /**
   * Individual Sample Form Component
   * @param {Object} props - Component props
   * @param {Object} props.sample - Sample data object
   * @param {number} props.index - Sample index in array
   * @param {Function} props.remove - Function to remove sample
   * @param {boolean} props.canRemove - Whether sample can be removed (false for first sample)
   */
  const SingleSampleForm = ({ sample, index, remove, canRemove,  values, 
  setFieldValue, userHierarchy }) => {
   if (!values || !values.TestSampleDetails || !values.TestSampleDetails[index]) {
    return null;
  }

  const sampleFieldName = (fieldName) => `TestSampleDetails[${index}].${fieldName}`;
  const currentSample = values.TestSampleDetails[index];

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const todayString = `${yyyy}-${mm}-${dd}`;
  const todayStorageString = `${dd}-${mm}-${yyyy}`;
  const hh = String(today.getHours()).padStart(2, '0');
  const min = String(today.getMinutes()).padStart(2, '0');
  const currentTimeString = `${hh}:${min}`;

  const isToday = sample.collectionDate === todayStorageString;
  const maxTime = isToday ? currentTimeString : undefined;

    return (
      <Card className="mb-4 sample-card">
        <Card.Header className="bg-light p-3 d-flex justify-content-between align-items-center">
          <h5 className="fw-bold mb-0 text-primary">
            Sample {index + 1}
          </h5>
          {canRemove && values.TestSampleDetails && values.TestSampleDetails.length > 1 && (
            <Button 
              variant="danger" 
              size="sm"
              onClick={() => remove(index)}
              title="Remove Sample"
            >
             <i className="fa fa-trash mr-1"></i> Remove
            </Button>
          )}
        </Card.Header>

        <Card.Body>
          {/* Sample Mapping Option */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">
                  <span style={{ color: "red", fontSize: "20px" }}>*</span> Sample Mapping Option :
                </label>
                <div className="">
                  <label className="mx-2">
                    <Field
                      type="radio"
                      name={sampleFieldName("sampleMappingOption")}
                      value="Add New Sample"
                    /> Add New Sample
                  </label>
                  {/* <label>
                    <Field
                      type="radio"
                      name={sampleFieldName("sampleMappingOption")}
                      value="Map Existing Sample"
                    /> Map Existing Sample
                  </label> */}
                </div>
              </div>
            </div>

            {/* Map Existing Sample Dropdown */}
            {currentSample.sampleMappingOption === 'Map Existing Sample' && (
              <div className="col-md-4">
                <div className="form-group">
                  <label>
                    <span style={{ color: "red", fontSize: "20px" }}>*</span>
                    Choose Sample ID
                  </label>
                  <Field
                    as="select"
                    name={sampleFieldName("mappedSampleId")}
                    className="form-control input-width select"
                  >
                    <option value="">Select Existing Sample</option>
                    {/* Add existing sample options here */}
                  </Field>
                </div>
              </div>
            )}
          </div>

          {/* New Sample Fields - Show only when "Add New Sample" is selected */}
          {currentSample.sampleMappingOption !== 'Map Existing Sample' && (
            <>
              {/* Sample Type and Details */}
              <div className="row mb-3">
                <SelectField
                name={sampleFieldName("sampleType")}
                label="Sample Type"
                required={true}
                colSize="col-md-4"
                placeholder="Select Sample Type"
                options={[
                  { value: "Sputum", label: "Sputum" },
                  { value: "Other", label: "Other" },
                ]}
              />

                {/* Conditional fields based on sample type */}
                {sample.sampleType === 'Sputum' && (
                  <>
                    <SelectField
                      name={sampleFieldName("sputumCollectionDetail")}
                      label="Sputum Collection Detail"
                      required={true}
                      colSize="col-md-4"
                      placeholder="Select Sputum Collection Detail"
                      options={[
                        { value: "Early Morning", label: "Early Morning" },
                        { value: "Spot", label: "Spot" },
                        { value: "Unknown", label: "Unknown" },
                      ]}
                    />

                    <SelectField
                      name={sampleFieldName("sampleDescription")}
                      label="Sample Description"
                      required={true}
                      colSize="col-md-4"
                      placeholder="Select Sample Description"
                      options={[
                        { value: "Mucopurulent", label: "Mucopurulent" },
                        { value: "Blood Stained", label: "Blood Stained" },
                        { value: "Saliva", label: "Saliva" },
                      ]}
                    />
                  </>
                )}

                {sample.sampleType === 'Other' && (
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        <span style={{ color: "red", fontSize: "20px" }}>*</span> Other
                      </label>
                      <Field 
                        name={sampleFieldName("sampleTypeOther")}
                        className="form-control" 
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Collection Date and Time */}
              <div className="row mb-3">
               <div className="col-md-4">
                  <DateField
                      name={sampleFieldName("collectionDate")}
                      label="Collection Date"
                      required={true}
                      max={todayString}
                  />
              </div>

               <div className="col-md-4">
                  <TimeField
                      name={sampleFieldName("collectionTime")}
                      label="Collection Time"
                      required={true}
                      max={maxTime}
                  />
              </div>

               <div className="col-md-4">
                  <DateField
                      name={sampleFieldName("referralDate")}
                      label="Referral Date"
                      required={false}
                      max={todayString}
                  />
              </div>
              </div>

              {/* Collection Site Information */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="form-label">
                      <span style={{ color: "red", fontSize: "20px" }}>*</span>
                      Collection Site State
                    </label>
                    <input
                      type="text"
                      className="form-control input-width"
                      value={sample.collectionSiteState || ""}
                      readOnly
                      disabled
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label className="form-label">
                      <span style={{ color: "red", fontSize: "20px" }}>*</span>
                      Collection Site District
                    </label>
                    <input
                      type="text"
                      className="form-control input-width"
                      value={sample.collectionSiteDistrict || ""}
                      readOnly
                      disabled
                    />
                  </div>
                </div>

                 <div className="col-md-4">
                    <HierarchyField
                        name={sampleFieldName("collectionSite")}
                        keyName={sampleFieldName("collectionSiteKey")}
                        levelName={sampleFieldName("collectionSiteLevel")}
                        label="Collection Site"
                        required={true}
                        placeholder="Select Collection Site"
                        apiMethod="getHierarchyFilters"
                        parentId={sample.collectionSiteDistrictKey}
                        setFieldValue={setFieldValue}
                        values={values}
                    />
                </div>
              </div>

              {/* Referral Site Information */}
              <div className="row mb-3">
               <div className="col-md-4">
                    <HierarchyField
                        name={sampleFieldName("referralSiteState")}
                        keyName={sampleFieldName("referralSiteStateKey")}
                        levelName={sampleFieldName("referralSiteStateLevel")}
                        label="Referral Site State (Optional)"
                        required={false}
                        placeholder="Select State"
                        apiMethod="getHierarchyChildren"
                        parentId={1}
                        setFieldValue={setFieldValue}
                        values={values}
                        staticOptionKey={userHierarchy?.collectionSiteStateKey}
                        staticOptionName={userHierarchy?.allowedState}
                        staticOptionLevel={userHierarchy?.collectionSiteStateLevel}
                        onChange={() => {
                          setFieldValue(sampleFieldName("referralSiteDistrict"), "");
                          setFieldValue(sampleFieldName("referralSiteDistrictKey"), "");
                          setFieldValue(sampleFieldName("referralSiteDistrictLevel"), null);
                          setFieldValue(sampleFieldName("referralSite"), "");
                          setFieldValue(sampleFieldName("referralSiteKey"), "");
                          setFieldValue(sampleFieldName("referralSiteLevel"), null);
                        }}
                    />
               </div>

               <div className="col-md-4">
                    <HierarchyField
                        name={sampleFieldName("referralSiteDistrict")}
                        keyName={sampleFieldName("referralSiteDistrictKey")}
                        levelName={sampleFieldName("referralSiteDistrictLevel")}
                        label="Referral Site District (Optional)"
                        required={false}
                        placeholder="Select District"
                        apiMethod="getHierarchyChildren"
                        parentId={sample.referralSiteStateKey}
                        setFieldValue={setFieldValue}
                        values={values}
                        staticOptionKey={userHierarchy?.collectionSiteDistrictKey}
                        staticOptionName={userHierarchy?.allowedDistrict}
                        staticOptionLevel={userHierarchy?.collectionSiteDistrictLevel}
                        onChange={() => {
                          setFieldValue(sampleFieldName("referralSite"), "");
                          setFieldValue(sampleFieldName("referralSiteKey"), "");
                          setFieldValue(sampleFieldName("referralSiteLevel"), null);
                        }}
                    />
               </div>

                 <div className="col-md-4">
                    <HierarchyField
                        name={sampleFieldName("referralSite")}
                        keyName={sampleFieldName("referralSiteKey")}
                        levelName={sampleFieldName("referralSiteLevel")}
                        label="Referral Site (Optional)"
                        required={false}
                        placeholder="Select Referral Site"
                        apiMethod="getHierarchyFilters"
                        parentId={sample.referralSiteDistrictKey}
                        setFieldValue={setFieldValue}
                        values={values}
                    />
                </div>
              </div>

              {/* Sample Serial ID */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="form-label">Sample Serial ID</label>
                    <Field 
                      type="text" 
                      name={sampleFieldName("sampleSerialId")}
                      className="form-control" 
                      placeholder="Enter Sample Serial ID" 
                    />
                  </div>
                </div>
              </div>

              {/* QR Code Section */}
              {/* <hr className="my-4" />
              <div className="row align-items-center">
                <div className="col-md-4 mb-3 mb-md-0">
                  <label className="form-label mb-0">Add QR code to the sample:</label>
                </div>
                <div className="col-md-4 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label">Sample QR Code</label>
                    <Field 
                      type="text" 
                      name={sampleFieldName("sampleQrCode")}
                      className="form-control" 
                      placeholder="Enter Sample QR Code" 
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center">
                    <div className="border p-3 d-inline-block">
                      <div className="text-muted mb-2">QR Code Preview</div>
                      {sample.sampleQrCode ? (
                        <QRCode value={sample.sampleQrCode} size={100} level="L" />
                      ) : (
                        <div className="bg-light p-4 d-flex align-items-center justify-content-center" style={{width: 100, height: 100}}>
                          <span className="text-muted">No QR</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div> */}
            </>
          )}

          {/* Mapped Sample Fields - Show only when "Map Existing Sample" is selected */}
          {currentSample.sampleMappingOption === 'Map Existing Sample' && (
            <div className="border p-3 mt-3 bg-light">
              <h6 className="text-info mb-3">Mapped Sample Information</h6>
              {/* Add mapped sample fields here if needed */}
              <p className="text-muted">Mapped sample details will be loaded automatically when a sample is selected.</p>
            </div>
          )}

        </Card.Body>
      </Card>
    );
  };

/**
 * SampleDetails Component - Handles multiple samples with add/remove functionality
 * @param {Object} props - Component props
 * @param {Object} props.values - Formik values object
 * @param {Function} props.setFieldValue - Formik setFieldValue function
 * @param {Object} props.stateDistrictMap - State district mapping data
 * @param {string} props.selectedState - Currently selected state
 * @param {Function} props.setSelectedState - Function to set selected state
 * @param {string} props.selectedDistrict - Currently selected district
 * @param {Function} props.setSelectedDistrict - Function to set selected district
 */
const SampleDetails = ({ 
  values, 
  setFieldValue,
  userHierarchy
}) => {



  return (
    <Card className="my-4">
      <Card.Header className="bg-light p-3">
        <h4 className="fw-bold mb-0 text-primary">Sample Details</h4>
      </Card.Header>
      <Card.Body>
        <FieldArray name="TestSampleDetails">
            {({ push, remove }) => (
                <div>
                    {/* Render each sample */}
                    {values.TestSampleDetails?.map((sample, index) => (
                      <SingleSampleForm
                        key={index}
                        sample={sample}
                        index={index}
                        remove={remove}
                        values={values} 
                        setFieldValue={setFieldValue}
                        userHierarchy={userHierarchy}
                        canRemove={values.TestSampleDetails.length > 1}
                    />
                    ))}
                    
                    {/* Add button */}
                    <div className="text-end mt-3">
                        <Button
                            type="button"
                            variant="primary"
                            onClick={() => push(createInitialSample(userHierarchy))}
                            className="me-2"
                        >
                            <i className="fa fa-plus-circle" aria-hidden="true"></i> Add Another Sample
                        </Button>
                    </div>
                </div>
            )}
        </FieldArray>
      </Card.Body>
    </Card>
  );
};

export default SampleDetails;